import { useState, useEffect, useCallback } from 'react'
import { MdAdd, MdMonitor } from 'react-icons/md'
import { SimulationCard } from '../../components/cards'
import { Button, LoadingSpinner, EmptyState, SearchBar, FilterBar } from '../../components/ui'
import { useSimulation } from '../../hooks/useSimulation'

function SimulationList({
  onViewSimulation,
  onStartSimulation,
  onPauseSimulation,
  onStopSimulation,
}) {
  const [activeSimulations, setActiveSimulations] = useState([])
  const [archivedSimulations, setArchivedSimulations] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [filter, setFilter] = useState('all') // all, active, archived
  const [isLoading, setIsLoading] = useState(true)

  const {
    getActiveSimulationsAsync,
    getArchivedSimulationsAsync,
    startSimulationAsync,
    stopSimulationAsync,
  } = useSimulation()

  const loadSimulations = useCallback(async () => {
    try {
      setIsLoading(true)
      const [activeData, archivedData] = await Promise.all([
        getActiveSimulationsAsync(),
        getArchivedSimulationsAsync(),
      ])

      setActiveSimulations(activeData || [])
      setArchivedSimulations(archivedData || [])
    } catch (error) {
      console.error('Error loading simulations:', error)
    } finally {
      setIsLoading(false)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadSimulations()
  }, [loadSimulations])

  const handleStartSimulation = async (simulation) => {
    try {
      await startSimulationAsync(simulation.simulationNumber || simulation.id)
      await loadSimulations() // Refresh the list
      if (onStartSimulation) {
        onStartSimulation(simulation)
      }
    } catch (error) {
      console.error('Error starting simulation:', error)
    }
  }

  const handleStopSimulation = async (simulation) => {
    try {
      await stopSimulationAsync(simulation.simulationNumber || simulation.id)
      await loadSimulations() // Refresh the list
      if (onStopSimulation) {
        onStopSimulation(simulation)
      }
    } catch (error) {
      console.error('Error stopping simulation:', error)
    }
  }

  const handlePauseSimulation = async (simulation) => {
    // For now, we'll use stop as pause since the API doesn't have a pause endpoint
    await handleStopSimulation(simulation)
    if (onPauseSimulation) {
      onPauseSimulation(simulation)
    }
  }

  const handleViewSimulation = (simulation) => {
    if (onViewSimulation) {
      onViewSimulation(simulation)
    }
  }

  const getFilteredSimulations = () => {
    let simulations = []

    if (filter === 'active') {
      simulations = activeSimulations
    } else if (filter === 'archived') {
      simulations = archivedSimulations
    } else {
      simulations = [...activeSimulations, ...archivedSimulations]
    }

    if (searchTerm) {
      simulations = simulations.filter(
        (simulation) =>
          simulation.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          simulation.simulationNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          simulation.status?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          simulation.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          simulation.tags?.some((tag) => tag.toLowerCase().includes(searchTerm.toLowerCase())),
      )
    }

    return simulations
  }

  const filteredSimulations = getFilteredSimulations()

  const getActiveCount = () => activeSimulations.length
  const getArchivedCount = () => archivedSimulations.length
  const getTotalCount = () => getActiveCount() + getArchivedCount()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header with search and filters */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <SearchBar
          searchTerm={searchTerm}
          onSearchChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search simulations..."
        />

        <div className="flex items-center space-x-3">
          <FilterBar
            filterValue={filter}
            onFilterChange={(e) => setFilter(e.target.value)}
            filterOptions={[
              { value: 'all', label: 'All Simulations' },
              { value: 'active', label: `Active (${getActiveCount()})` },
              { value: 'archived', label: `Archived (${getArchivedCount()})` },
            ]}
          />

          <Button
            variant="primary"
            size="md"
            onClick={() => {
              /* TODO: Implement create simulation */
            }}
            className="flex items-center space-x-2"
          >
            <MdAdd className="text-lg" />
            <span>New Simulation</span>
          </Button>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                Active Simulations
              </p>
              <p className="text-2xl font-bold text-neutral-900 dark:text-white mt-1">
                {getActiveCount()}
              </p>
            </div>
            <div className="w-12 h-12 bg-success-100 dark:bg-success-900 rounded-lg flex items-center justify-center">
              <MdMonitor className="text-success-600 dark:text-success-400 text-xl" />
            </div>
          </div>
        </div>

        <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                Total Simulations
              </p>
              <p className="text-2xl font-bold text-neutral-900 dark:text-white mt-1">
                {getTotalCount()}
              </p>
            </div>
            <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center">
              <MdMonitor className="text-primary-600 dark:text-primary-400 text-xl" />
            </div>
          </div>
        </div>

        <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">Archived</p>
              <p className="text-2xl font-bold text-neutral-900 dark:text-white mt-1">
                {getArchivedCount()}
              </p>
            </div>
            <div className="w-12 h-12 bg-neutral-100 dark:bg-neutral-800 rounded-lg flex items-center justify-center">
              <MdMonitor className="text-neutral-600 dark:text-neutral-400 text-xl" />
            </div>
          </div>
        </div>
      </div>

      {/* Simulations Grid */}
      {filteredSimulations.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredSimulations.map((simulation) => (
            <SimulationCard
              key={simulation.id || simulation.simulationNumber}
              simulation={simulation}
              onView={handleViewSimulation}
              onStart={handleStartSimulation}
              onPause={handlePauseSimulation}
              onStop={handleStopSimulation}
            />
          ))}
        </div>
      ) : (
        <EmptyState
          icon={<MdMonitor />}
          title="No simulations found"
          description={
            searchTerm
              ? `No simulations match "${searchTerm}". Try adjusting your search.`
              : filter === 'active'
                ? 'No active simulations at the moment.'
                : filter === 'archived'
                  ? 'No archived simulations found.'
                  : 'No simulations have been created yet.'
          }
          action={
            <Button
              variant="primary"
              onClick={() => {
                /* TODO: Implement create simulation */
              }}
            >
              Create First Simulation
            </Button>
          }
        />
      )}
    </div>
  )
}

export default SimulationList
